// ─── Shared API fetch helper ──────────────────────────────────────────
import { createLogger } from "../lib/logger";
import { authService } from "./authService";
import type { AppSummary } from "../types/apps";
import { isAppSummaryList } from "../types/apps";
import { runtimeConfig } from "../runtimeConfig";

const slog = createLogger("api");
const API_BASE = runtimeConfig.apiBaseUrl;

/** Authenticated fetch wrapper — injects Bearer token + JSON headers. */
export async function apiFetch(
  path: string,
  options?: RequestInit,
): Promise<Response> {
  const method = options?.method ?? "GET";
  const t0 = performance.now();
  const token = authService.getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  const ms = Math.round(performance.now() - t0);
  slog.api(method, path, res.status, ms);

  return res;
}

/**
 * Check response and log errors with detail extraction.
 * Usage: const data = await verboseCheck(await apiFetch("/sessions"), "list sessions");
 */
export async function verboseCheck(
  res: Response,
  context: string,
): Promise<Response> {
  if (res.ok) return res;

  let detail = "";
  try {
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("json")) {
      const body = await res.json();
      detail = body.error || body.message || body.detail || JSON.stringify(body).slice(0, 500);
    } else {
      detail = (await res.text()).slice(0, 500);
    }
  } catch {
    detail = "(could not read error body)";
  }

  const src = detectSource(res.url);
  slog.error(`${context} failed`, {
    status: res.status,
    url: res.url.replace(/token=[^&]+/g, "token=***"),
    detail,
    src,
  });

  throw new Error(`${context}: ${res.status} ${detail}`);
}

function detectSource(url: string): string {
  if (url.includes("/contexts")) return "contexts";
  if (url.includes("/apps")) return "apps";
  if (url.includes("/sessions")) return "session";
  if (url.includes("/logs")) return "logs";
  if (url.includes("/turn")) return "turn";
  if (url.includes("/signaling")) return "signaling";
  return "api";
}

// ── Convenience helpers ──────────────────────────────────────────

export async function stopSession(sessionId: string): Promise<Response> {
  return apiFetch(`/sessions/${sessionId}/stop`, { method: "POST" });
}

export async function startSession(sessionId: string): Promise<Response> {
  return apiFetch(`/sessions/${sessionId}/start`, { method: "POST" });
}

export async function disconnectSession(sessionId: string): Promise<Response> {
  return apiFetch(`/sessions/${sessionId}/disconnect`, { method: "POST" });
}

// Backwards-compatible aliases
export async function freezeSession(sessionId: string): Promise<Response> {
  return stopSession(sessionId);
}

export async function thawSession(sessionId: string): Promise<Response> {
  return startSession(sessionId);
}

export async function renameSession(
  sessionId: string,
  name: string,
): Promise<Response> {
  return apiFetch(`/sessions/${sessionId}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export async function getSessionToken(
  sessionId: string,
): Promise<string | null> {
  try {
    const res = await apiFetch(`/sessions/${sessionId}/token`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.session_token ?? null;
  } catch {
    return null;
  }
}

export async function uploadThumbnail(
  sessionId: string,
  dataUrl: string,
): Promise<void> {
  await apiFetch(`/sessions/${sessionId}/thumbnail`, {
    method: "POST",
    body: JSON.stringify({ data: dataUrl }),
  }).catch(() => {});
}

export async function fetchApps(): Promise<AppSummary[]> {
  try {
    const res = await apiFetch("/apps");
    if (!res.ok) return [];
    const data: unknown = await res.json();
    return isAppSummaryList(data) ? data : [];
  } catch {
    return [];
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// File transfer API (T114)
// ──────────────────────────────────────────────────────────────────────────────

export interface FileItem {
  name: string;
  path: string;
  size: number;
  modified: string;
}

export interface FileQuotaResponse {
  enabled?: boolean;
  session: { used_bytes: number; limit_bytes: number | null; remaining_bytes: number | null };
  daily_user: { used_bytes: number; limit_bytes: number | null; remaining_bytes: number | null };
  per_file_limit_bytes: number | null;
}

export async function listFilesApi(
  sessionId: string,
  path = "",
  offset = 0,
  limit = 50,
): Promise<{ items: FileItem[]; total: number; offset: number; limit: number }> {
  const params = new URLSearchParams({ path, offset: String(offset), limit: String(limit) });
  const res = await apiFetch(`/sessions/${sessionId}/files/list?${params}`);
  if (!res.ok) throw new Error(`list files: ${res.status}`);
  return res.json() as Promise<{ items: FileItem[]; total: number; offset: number; limit: number }>;
}

export async function deleteFileApi(sessionId: string, path: string): Promise<void> {
  const params = new URLSearchParams({ path });
  const res = await apiFetch(`/sessions/${sessionId}/files?${params}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`delete file: ${res.status}`);
}

export async function getFileQuotaApi(sessionId: string): Promise<FileQuotaResponse> {
  const res = await apiFetch(`/sessions/${sessionId}/files/quota`);
  if (!res.ok) throw new Error(`get quota: ${res.status}`);
  return res.json() as Promise<FileQuotaResponse>;
}

export async function downloadFileApi(
  sessionId: string,
  path: string,
  token: string,
): Promise<void> {
  const params = new URLSearchParams({ path });
  const res = await fetch(`${API_BASE}/sessions/${sessionId}/files/download?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`download: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = path.split("/").pop() ?? "file";
  a.click();
  URL.revokeObjectURL(url);
}

export function uploadFileXhr(
  sessionId: string,
  file: File,
  token: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file);
    form.append("path", file.name);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/sessions/${sessionId}/files/upload`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload: ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error("upload network error"));
    xhr.send(form);
  });
}
